import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { DollarSign, TrendingUp, Rocket, Shield, Zap, ArrowRight } from 'lucide-react';
import NavigationControls from '../components/NavigationControls';
import PartieBadge from '../components/PartieBadge';
import { useLanguage } from '../contexts/LanguageContext';
import { preLaunchBudget, LICORNE_YEAR, valorisation, preSeedInvestment } from '../mock';
import { formatCurrency, formatCompact } from '../lib/utils';

export default function LeveeFondsPage() {
  const { language } = useLanguage();
  const licorneVal = valorisation.find(v => v.annee === LICORNE_YEAR) || {};
  const totalInvestissement = preSeedInvestment.total;

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-8">
      <PartieBadge path="/levee-de-fonds" />
      <Card className="bg-black/60 border-lime-500/30 backdrop-blur-sm">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-lime-500/20 rounded-lg">
              <DollarSign className="h-6 w-6 text-lime-400" />
            </div>
            <CardTitle className="text-white text-2xl" data-testid="levee-fonds-title">
              {language === 'fr' ? 'Levée de Fonds & Financement' : 'Fundraising & Financing'}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-8">

          {/* Investissement Initial */}
          <div className="p-6 bg-gradient-to-br from-lime-500/10 to-transparent rounded-xl border-2 border-lime-500/30">
            <h3 className="text-2xl font-bold text-lime-400 mb-4" data-testid="investissement-initial-title">
              {language === 'fr' ? 'Pré-seed 500K — Use of Funds' : 'Pre-seed $500K — Use of Funds'}
            </h3>
            <div className="text-center mb-6">
              <p className="text-3xl sm:text-5xl font-bold text-lime-400" data-testid="total-investissement">
                {formatCurrency(totalInvestissement)}
              </p>
              <p className="text-gray-400 mt-2">
                {language === 'fr' ? 'Pré-seed 6 mois — ronde seed Février' : 'Pre-seed 6 months — seed round February'}
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="p-3 bg-black/40 rounded-lg border border-lime-500/20 text-center" data-testid="uof-gtm">
                <p className="text-xs text-gray-400 mb-1">GTM</p>
                <p className="text-base sm:text-lg font-bold text-lime-400">{formatCurrency(preSeedInvestment.useOfFunds.gtm)}</p>
                <p className="text-[10px] text-gray-500">{language === 'fr' ? '3 vendeurs + closer' : '3 reps + closer'}</p>
              </div>
              <div className="p-3 bg-black/40 rounded-lg border border-cyan-500/20 text-center" data-testid="uof-dev">
                <p className="text-xs text-gray-400 mb-1">{language === 'fr' ? 'Développement' : 'Development'}</p>
                <p className="text-base sm:text-lg font-bold text-cyan-400">{formatCurrency(preSeedInvestment.useOfFunds.development)}</p>
              </div>
              <div className="p-3 bg-black/40 rounded-lg border border-orange-500/20 text-center" data-testid="uof-ledger">
                <p className="text-xs text-gray-400 mb-1">Ledger / MOR</p>
                <p className="text-base sm:text-lg font-bold text-orange-400">{formatCurrency(preSeedInvestment.useOfFunds.ledger)}</p>
              </div>
              <div className="p-3 bg-black/40 rounded-lg border border-purple-500/20 text-center" data-testid="uof-legal">
                <p className="text-xs text-gray-400 mb-1">Legal</p>
                <p className="text-base sm:text-lg font-bold text-purple-400">{formatCurrency(preSeedInvestment.useOfFunds.legal)}</p>
              </div>
              <div className="p-3 bg-black/40 rounded-lg border border-cyan-500/20 text-center" data-testid="uof-mkt">
                <p className="text-xs text-gray-400 mb-1">Marketing</p>
                <p className="text-base sm:text-lg font-bold text-cyan-400">{formatCurrency(preSeedInvestment.useOfFunds.marketing)}</p>
              </div>
              <div className="p-3 bg-black/40 rounded-lg border border-orange-500/20 text-center" data-testid="uof-reserve">
                <p className="text-xs text-gray-400 mb-1">{language === 'fr' ? 'Réserve' : 'Reserve'}</p>
                <p className="text-base sm:text-lg font-bold text-orange-400">{formatCurrency(preSeedInvestment.useOfFunds.reserve)}</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3 text-center">
              {language === 'fr'
                ? '~10% equity • 6 mois (Sept → Fév) • Ronde seed planifiée Février'
                : '~10% equity • 6 months (Sep → Feb) • Seed round planned February'}
            </p>
          </div>

          {/* Autonomie Financière Rapide */}
          <div className="p-6 bg-gradient-to-br from-cyan-500/10 to-transparent rounded-xl border border-cyan-500/30">
            <h3 className="text-2xl font-bold text-cyan-400 mb-4" data-testid="autonomie-title">
              {language === 'fr' ? 'Autonomie Financière Rapide' : 'Rapid Financial Autonomy'}
            </h3>
            <p className="text-gray-300 leading-relaxed mb-6">
              {language === 'fr'
                ? `Le financement initial (${formatCurrency(totalInvestissement)}) couvre exclusivement la phase de mise en opération (M0-M6). Dès le mois 6-7, EcoLunch atteint l'équilibre puis une rentabilité opérationnelle, permettant de financer l'ensemble de ses opérations et de sa croissance sans recours à du financement externe additionnel.`
                : `Initial funding (${formatCurrency(totalInvestissement)}) covers exclusively the operational setup phase (M0-M6). As of month 6-7, EcoLunch reaches break-even then operational profitability, enabling it to fund all operations and growth without additional external financing.`}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 sm:p-5 bg-black/40 rounded-xl border border-orange-500/30 text-center" data-testid="phase-m0m6">
                <p className="text-xl sm:text-2xl font-bold text-orange-400 mb-2">M0-M6</p>
                <p className="text-sm text-gray-300">{language === 'fr' ? 'Phase financement' : 'Funding phase'}</p>
                <p className="text-lg font-bold text-orange-300 mt-1">{formatCurrency(totalInvestissement)}</p>
              </div>
              <div className="p-4 sm:p-5 bg-black/40 rounded-xl border border-cyan-500/30 text-center" data-testid="phase-m6m7">
                <p className="text-xl sm:text-2xl font-bold text-cyan-400 mb-2">M6-M7</p>
                <p className="text-sm text-gray-300">{language === 'fr' ? 'Équilibre atteint' : 'Break-even reached'}</p>
                <p className="text-sm text-lime-400 mt-1">{language === 'fr' ? 'Cashflow positif' : 'Positive cashflow'}</p>
              </div>
              <div className="p-4 sm:p-5 bg-black/40 rounded-xl border border-lime-500/30 text-center" data-testid="phase-m7plus">
                <p className="text-xl sm:text-2xl font-bold text-lime-400 mb-2">M7+</p>
                <p className="text-sm text-gray-300">{language === 'fr' ? 'Croissance autofinancée' : 'Self-funded growth'}</p>
                <p className="text-sm text-lime-300 mt-1">{language === 'fr' ? 'Financement opportuniste' : 'Opportunistic financing'}</p>
              </div>
            </div>
          </div>

          {/* Financement Opportuniste */}
          <div className="p-6 bg-gradient-to-br from-orange-500/10 to-transparent rounded-xl border border-orange-500/30">
            <div className="flex items-center gap-3 mb-4">
              <Rocket className="h-7 w-7 text-orange-400" />
              <h3 className="text-2xl font-bold text-orange-400" data-testid="opportuniste-title">
                {language === 'fr' ? 'Financement Opportuniste' : 'Opportunistic Financing'}
              </h3>
            </div>
            
            <div className="p-5 bg-black/40 rounded-xl border border-orange-500/20 mb-6">
              <p className="text-gray-300 leading-relaxed">
                {language === 'fr'
                  ? `Le plan d'affaires d'EcoLunch est conçu pour atteindre ses objectifs — incluant le statut de Licorne en ${LICORNE_YEAR} (${formatCompact(licorneVal.valeur || 0)}) — sans aucun financement additionnel au-delà de l'investissement initial de ${formatCurrency(totalInvestissement)}. La croissance est entièrement autofinancée par les revenus récurrents dès M7.`
                  : `EcoLunch's business plan is designed to reach its goals — including Unicorn status by ${LICORNE_YEAR} (${formatCompact(licorneVal.valeur || 0)}) — without any additional financing beyond the initial ${formatCurrency(totalInvestissement)} investment. Growth is entirely self-funded through recurring revenues from M7 onward.`}
              </p>
            </div>

            <div className="p-5 bg-gradient-to-r from-lime-500/10 to-cyan-500/10 rounded-xl border border-lime-500/30">
              <h4 className="text-lg font-bold text-lime-400 mb-3">
                {language === 'fr' ? 'Toutefois, un financement opportuniste pourrait :' : 'However, opportunistic funding could:'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Zap className="h-5 w-5 text-lime-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-300">
                    {language === 'fr' ? 'Accélérer l\'expansion géographique vers de nouvelles provinces en devançant le calendrier prévu' : 'Accelerate geographic expansion to new provinces ahead of schedule'}
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <TrendingUp className="h-5 w-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-300">
                    {language === 'fr' ? 'Intensifier les efforts d\'acquisition (marketing, incitatifs écoles) pour une pénétration plus rapide du marché' : 'Intensify acquisition efforts (marketing, school incentives) for faster market penetration'}
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-300">
                    {language === 'fr' ? 'Renforcer les barrières concurrentielles en consolidant le monopole régional avant l\'arrivée de compétiteurs' : 'Strengthen competitive barriers by consolidating regional monopoly before competitors arrive'}
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Rocket className="h-5 w-5 text-lime-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-300">
                    {language === 'fr' ? 'Atteindre le statut de Licorne plus tôt que prévu, maximisant le retour pour les investisseurs' : 'Reach Unicorn status sooner than projected, maximizing investor returns'}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-black/40 rounded-lg border border-gray-700 text-center">
              <p className="text-sm text-gray-400 italic">
                {language === 'fr'
                  ? 'Aucun de ces accélérateurs n\'est un besoin impératif. Le plan atteint ses objectifs de manière autonome. Tout financement additionnel est une opportunité d\'accélération, pas une nécessité.'
                  : 'None of these accelerators are imperative needs. The plan reaches its objectives autonomously. Any additional funding is an acceleration opportunity, not a necessity.'}
              </p>
            </div>
          </div>

          <NavigationControls />
        </CardContent>
      </Card>
    </div>
  );
}
